import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSystemRunnerModel,
  deleteSystemRunnerModel,
  fetchMergedRunnerModels,
  fetchRunnerModels,
  fetchRunnerModelsForDaemon,
  fetchSystemRunnerModels,
  updateSystemRunnerModel,
  type CreateRunnerModelInput,
  type PagedData,
  type MergedRunnerModelsByType,
  type RunnerModelQueryParams,
  type RunnerModelRecord,
  type UpdateRunnerModelInput,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

export const useRunnerModels = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.runnerModels.public,
    queryFn: fetchRunnerModels,
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    // 세션 내 불변 목록: 모달을 닫아도 캐시를 유지해 재오픈 시 재요청하지 않는다.
    gcTime: Infinity,
  });
};

export const useMergedRunnerModels = (
  token: string | null,
  memberId: string | null,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.runnerModels.merged(memberId ?? ''),
    queryFn: () => fetchMergedRunnerModels(token as string, memberId as string),
    enabled: Boolean(token) && Boolean(memberId) && (options?.enabled ?? true),
    staleTime: 300_000,
    gcTime: 1_800_000,
    placeholderData: keepPreviousData,
  });
};

export const useRunnerModelCatalogs = (projectId: string) => {
  return useQuery({
    queryKey: queryKeys.runnerModels.catalogs(projectId),
    queryFn: async (): Promise<Record<string, MergedRunnerModelsByType>> => {
      throw new Error('runner model catalogs are hydrated from bootstrap');
    },
    enabled: false,
    staleTime: 60_000,
    gcTime: 1_800_000,
  });
};

export const useRunnerModelsForDaemon = (
  token: string | null,
  projectId: string,
  daemonId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.runnerModels.forDaemon(projectId, daemonId),
    queryFn: () => fetchRunnerModelsForDaemon(token as string, projectId, daemonId),
    enabled: Boolean(token) && projectId.length > 0 && daemonId.length > 0 && (options?.enabled ?? true),
    staleTime: 300_000,
    gcTime: 1_800_000,
  });
};

export const useSystemRunnerModelsPage = (token: string | null, params?: RunnerModelQueryParams) => {
  return useQuery({
    queryKey: queryKeys.runnerModels.systemList(params ?? {}),
    queryFn: () => fetchSystemRunnerModels(token as string, params),
    enabled: Boolean(token),
    staleTime: 300_000,
  });
};

type SystemRunnerModelsInfiniteParams = Omit<RunnerModelQueryParams, 'page' | 'pageSize'>;

export const useSystemRunnerModelsInfinite = (
  token: string | null,
  params: SystemRunnerModelsInfiniteParams = {},
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery<
    PagedData<RunnerModelRecord>,
    Error,
    PagedData<RunnerModelRecord>,
    ReturnType<typeof queryKeys.runnerModels.systemInfinite>,
    number
  >({
    queryKey: queryKeys.runnerModels.systemInfinite(params),
    queryFn: ({ pageParam }) => fetchSystemRunnerModels(token as string, { ...params, page: pageParam, pageSize: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(token) && (options?.enabled ?? true),
    staleTime: 300_000,
    select: (data) =>
      ({
        pages: data.pages,
        pageParams: data.pageParams,
        data: data.pages.flatMap((page) => page.data),
        meta: data.pages[data.pages.length - 1]?.meta,
      }) as unknown as PagedData<RunnerModelRecord>,
  });
};

export const useCreateSystemRunnerModelMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRunnerModelInput) => createSystemRunnerModel(token as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};

export const useUpdateSystemRunnerModelMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRunnerModelInput }) =>
      updateSystemRunnerModel(token as string, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};

export const useDeleteSystemRunnerModelMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSystemRunnerModel(token as string, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};
